/**
 * Customer Portal (UI) - settings route with tabbed configuration panels.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { CallbackProfileModal } from '../components/webhooks/CallbackProfileModal';
import { ConfirmDeleteModal } from '../components/webhooks/ConfirmDeleteModal';
import { EventAssignmentModal } from '../components/webhooks/EventAssignmentModal';
import {
  assignmentScopeOptions,
  createAssignmentDraft,
  createProfileDraft
} from '../components/webhooks/webhookDrafts';
import { TemplateAssignmentModal } from '../components/templates/TemplateAssignmentModal';
import {
  deleteCallbackProfile,
  deleteWebhookEventAssignment,
  fetchBrands,
  fetchCallbackProfiles,
  fetchCampaigns,
  fetchPhoneNumbers,
  fetchWebhookEventAssignments,
  fetchWebhookTypes,
  upsertCallbackProfile,
  upsertWebhookEventAssignment
} from '../features/api/client';
import type {
  Brand,
  CallbackProfile,
  CallbackProfileUpsertInput,
  Campaign,
  PhoneNumber,
  WebhookEventAssignment,
  WebhookEventAssignmentUpsertInput,
  WebhookTypeCatalogItem
} from '../types/portal';

type SettingsRouteProps = {
  accountId: string | null;
};

export function SettingsRoute({ accountId }: SettingsRouteProps) {
  const [activeTab, setActiveTab] = useState<'webhooks' | 'templateAssignment'>('webhooks');
  const [webhookSubTab, setWebhookSubTab] = useState<'assignments' | 'profiles'>('assignments');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<CallbackProfile[]>([]);
  const [assignments, setAssignments] = useState<WebhookEventAssignment[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [webhookTypes, setWebhookTypes] = useState<WebhookTypeCatalogItem[]>([]);

  const [profileDraft, setProfileDraft] = useState<CallbackProfileDraft>(() => createProfileDraft());
  const [assignmentDraft, setAssignmentDraft] = useState<EventAssignmentDraft>(() => createAssignmentDraft(accountId ?? ''));
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const [deletingAssignment, setDeletingAssignment] = useState(false);
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);
  const [deleteAssignmentId, setDeleteAssignmentId] = useState<string | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const profileById = useMemo(() => new Map(profiles.map((profile) => [profile.profileId, profile])), [profiles]);
  const webhookTypeById = useMemo(() => new Map(webhookTypes.map((type) => [type.id, type])), [webhookTypes]);
  const brandById = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);
  const campaignById = useMemo(() => new Map(campaigns.map((campaign) => [campaign.id, campaign])), [campaigns]);
  const phoneNumberById = useMemo(() => new Map(phoneNumbers.map((phone) => [phone.id, phone])), [phoneNumbers]);
  const defaultWebhookType = webhookTypes[0]?.id ?? 'delivery_report';

  useEffect(() => {
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchCallbackProfiles(accountId),
      fetchWebhookEventAssignments(accountId),
      fetchWebhookTypes(),
      fetchBrands(accountId),
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, { brandId: null, campaignId: null, phoneNumberId: null })
    ])
      .then(([profileResponse, assignmentResponse, webhookTypesResponse, brandsResponse, campaignResponse, phoneResponse]) => {
        if (cancelled) return;
        setProfiles(profileResponse.items);
        setAssignments(assignmentResponse.items);
        setWebhookTypes(webhookTypesResponse.items);
        setBrands(brandsResponse.items);
        setCampaigns(campaignResponse.items);
        setPhoneNumbers(phoneResponse.items);
        setAssignmentDraft((draft) => ({
          ...draft,
          webhookType: draft.webhookType || (webhookTypesResponse.items[0]?.id ?? draft.webhookType),
          scopeId: draft.scopeType === 'ACCOUNT' ? accountId : draft.scopeId
        }));
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError instanceof Error ? requestError.message : String(requestError));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  async function refreshWebhookData(): Promise<void> {
    if (!accountId) return;
    const [profileResponse, assignmentResponse] = await Promise.all([
      fetchCallbackProfiles(accountId),
      fetchWebhookEventAssignments(accountId)
    ]);
    setProfiles(profileResponse.items);
    setAssignments(assignmentResponse.items);
  }

  function openNewProfileModal(): void {
    setProfileDraft(createProfileDraft());
    setProfileModalOpen(true);
  }

  function openEditProfileModal(profile: CallbackProfile): void {
    setProfileDraft(createProfileDraft(profile));
    setProfileModalOpen(true);
  }

  function openNewAssignmentModal(): void {
    setAssignmentDraft(createAssignmentDraft(accountId ?? '', undefined, defaultWebhookType));
    setAssignmentModalOpen(true);
  }

  function openEditAssignmentModal(assignment: WebhookEventAssignment): void {
    setAssignmentDraft(createAssignmentDraft(accountId ?? '', assignment, defaultWebhookType));
    setAssignmentModalOpen(true);
  }

  async function handleSaveProfile(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!accountId) return;
    setSavingProfile(true);
    setError(null);
    try {
      const payload: CallbackProfileUpsertInput = {
        profileId: profileDraft.profileId,
        name: profileDraft.name,
        description: profileDraft.description || null,
        url: profileDraft.url,
        method: profileDraft.method,
        enabled: profileDraft.enabled,
        contentType: profileDraft.contentType || 'application/json',
        authType: profileDraft.authType,
        authBasicUsername: profileDraft.authBasicUsername || null,
        authApiKeyHeader: profileDraft.authApiKeyHeader || null,
        authHmacHeader: profileDraft.authHmacHeader || null,
        authHmacTimestampHeader: profileDraft.authHmacTimestampHeader || null,
        authHmacIdHeader: profileDraft.authHmacIdHeader || null,
        authHmacPrefix: profileDraft.authHmacPrefix || null,
        authHmacIncludeTimestamp: profileDraft.authHmacIncludeTimestamp
      };

      if (profileDraft.authBearerToken.trim()) payload.authBearerToken = profileDraft.authBearerToken.trim();
      if (profileDraft.authBasicPassword.trim()) payload.authBasicPassword = profileDraft.authBasicPassword.trim();
      if (profileDraft.authApiKeyValue.trim()) payload.authApiKeyValue = profileDraft.authApiKeyValue.trim();
      if (profileDraft.authHmacSecret.trim()) payload.authHmacSecret = profileDraft.authHmacSecret.trim();

      await upsertCallbackProfile(accountId, payload);
      await refreshWebhookData();
      setProfileModalOpen(false);
      setProfileDraft(createProfileDraft());
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleDeleteProfile(): Promise<void> {
    if (!accountId || !deleteProfileId) return;
    setDeletingProfile(true);
    setError(null);
    try {
      await deleteCallbackProfile(accountId, deleteProfileId);
      await refreshWebhookData();
      if (profileDraft.profileId === deleteProfileId) {
        setProfileDraft(createProfileDraft());
      }
      setDeleteProfileId(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setDeletingProfile(false);
    }
  }

  async function handleSaveAssignment(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!accountId) return;
    setSavingAssignment(true);
    setError(null);
    try {
      const payload: WebhookEventAssignmentUpsertInput = {
        assignmentId: assignmentDraft.assignmentId,
        webhookType: assignmentDraft.webhookType,
        scopeType: assignmentDraft.scopeType,
        scopeId: assignmentDraft.scopeType === 'ACCOUNT' ? accountId : assignmentDraft.scopeId,
        profileId: assignmentDraft.profileId,
        enabled: assignmentDraft.enabled,
        priority: assignmentDraft.priority
      };
      await upsertWebhookEventAssignment(accountId, payload);
      await refreshWebhookData();
      setAssignmentModalOpen(false);
      setAssignmentDraft(createAssignmentDraft(accountId, undefined, defaultWebhookType));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setSavingAssignment(false);
    }
  }

  async function handleDeleteAssignment(): Promise<void> {
    if (!accountId || !deleteAssignmentId) return;
    setDeletingAssignment(true);
    setError(null);
    try {
      await deleteWebhookEventAssignment(accountId, { assignmentId: deleteAssignmentId });
      await refreshWebhookData();
      if (assignmentDraft.assignmentId === deleteAssignmentId) {
        setAssignmentDraft(createAssignmentDraft(accountId, undefined, defaultWebhookType));
      }
      setDeleteAssignmentId(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : String(requestError));
    } finally {
      setDeletingAssignment(false);
    }
  }

  const scopeOptions = assignmentScopeOptions(assignmentDraft.scopeType, {
    accountId,
    brands,
    campaigns,
    phoneNumbers
  });
  const webhookTypeOptions = useMemo(
    () =>
      webhookTypes.map((item) => ({
        value: item.id,
        label: item.name
      })),
    [webhookTypes]
  );

  function resolveScopeLabel(assignment: WebhookEventAssignment): string {
    if (assignment.scopeType === 'ACCOUNT') {
      return accountId ?? assignment.scopeId;
    }
    if (assignment.scopeType === 'BRAND') {
      return brandById.get(assignment.scopeId)?.name ?? assignment.scopeId;
    }
    if (assignment.scopeType === 'CAMPAIGN') {
      return campaignById.get(assignment.scopeId)?.name ?? assignment.scopeId;
    }
    const phone = phoneNumberById.get(assignment.scopeId);
    return phone ? `${phone.label} (${phone.e164})` : assignment.scopeId;
  }

  function renderEnabledBadge(enabled: boolean): JSX.Element {
    return enabled ? (
      <span className="badge bg-green-lt text-green">Enabled</span>
    ) : (
      <span className="badge bg-secondary-lt text-secondary">Disabled</span>
    );
  }

  return (
    <>
      <div className="page-pretitle">Configuration</div>
      <h2 className="page-title mb-3">Settings</h2>
      {error ? <div className="alert alert-danger mb-3">{error}</div> : null}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'webhooks' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveTab('webhooks')}
          >
            Webhooks
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'templateAssignment' ? 'active' : ''}`}
            type="button"
            onClick={() => setActiveTab('templateAssignment')}
          >
            Template assignment
          </button>
        </li>
      </ul>

      {activeTab === 'webhooks' ? (
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <ul className="nav nav-tabs card-header-tabs mb-0" role="tablist">
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link${webhookSubTab === 'assignments' ? ' active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={webhookSubTab === 'assignments'}
                  onClick={() => setWebhookSubTab('assignments')}
                >
                  Event Assignments
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link${webhookSubTab === 'profiles' ? ' active' : ''}`}
                  type="button"
                  role="tab"
                  aria-selected={webhookSubTab === 'profiles'}
                  onClick={() => setWebhookSubTab('profiles')}
                >
                  Callback Profiles
                </button>
              </li>
            </ul>
            {webhookSubTab === 'assignments' ? (
              <button className="btn btn-sm btn-primary" type="button" onClick={openNewAssignmentModal} disabled={!accountId}>
                New Assignment
              </button>
            ) : (
              <button className="btn btn-sm btn-primary" type="button" onClick={openNewProfileModal} disabled={!accountId}>
                New Profile
              </button>
            )}
          </div>

          {webhookSubTab === 'assignments' ? (
            <div className="table-responsive">
              <table className="table card-table table-vcenter">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Scope</th>
                    <th>Profile</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((assignment) => (
                    <tr key={assignment.assignmentId}>
                      <td>{webhookTypeById.get(assignment.webhookType)?.name ?? assignment.webhookType}</td>
                      <td>
                        {assignment.scopeType} / {resolveScopeLabel(assignment)}
                      </td>
                      <td>{profileById.get(assignment.profileId)?.name ?? assignment.profileId}</td>
                      <td>{assignment.priority}</td>
                      <td>{renderEnabledBadge(assignment.enabled)}</td>
                      <td className="text-end">
                        <div className="btn-list justify-content-end">
                          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => openEditAssignmentModal(assignment)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            onClick={() => setDeleteAssignmentId(assignment.assignmentId)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!assignments.length ? (
                    <tr>
                      <td colSpan={6} className="text-secondary text-center py-4">
                        {loading ? 'Loading event assignments...' : 'No event assignments configured.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}

          {webhookSubTab === 'profiles' ? (
            <div className="table-responsive">
              <table className="table card-table table-vcenter">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Endpoint</th>
                    <th>Method</th>
                    <th>Auth</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((profile) => (
                    <tr key={profile.profileId}>
                      <td className="fw-semibold">{profile.name}</td>
                      <td className="text-secondary">{profile.url}</td>
                      <td>{profile.method}</td>
                      <td>{profile.authType}</td>
                      <td>{renderEnabledBadge(profile.enabled)}</td>
                      <td className="text-end">
                        <div className="btn-list justify-content-end">
                          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => openEditProfileModal(profile)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            type="button"
                            onClick={() => setDeleteProfileId(profile.profileId)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!profiles.length ? (
                    <tr>
                      <td colSpan={6} className="text-secondary text-center py-4">
                        {loading ? 'Loading callback profiles...' : 'No callback profiles configured.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {activeTab === 'templateAssignment' ? (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h3 className="card-title mb-0">Account template assignments</h3>
            <button className="btn btn-primary btn-sm" type="button" onClick={() => setTemplateModalOpen(true)}>
              Manage template assignments
            </button>
          </div>
          <div className="card-body">
            <p className="text-secondary mb-0">
              Assign templates at the account scope. Campaign and phone scopes inherit from these assignments.
            </p>
          </div>
        </div>
      ) : null}

      <TemplateAssignmentModal
        isOpen={templateModalOpen}
        scopeType="ACCOUNT"
        scopeId={accountId}
        title="Assign templates - account scope"
        onClose={() => setTemplateModalOpen(false)}
      />

      <CallbackProfileModal
        isOpen={profileModalOpen}
        draft={profileDraft}
        saving={savingProfile}
        accountId={accountId}
        onClose={() => setProfileModalOpen(false)}
        onSubmit={handleSaveProfile}
        setDraft={setProfileDraft}
      />

      <EventAssignmentModal
        isOpen={assignmentModalOpen}
        draft={assignmentDraft}
        accountId={accountId}
        saving={savingAssignment}
        profiles={profiles}
        webhookTypeOptions={webhookTypeOptions}
        scopeOptions={scopeOptions}
        onClose={() => setAssignmentModalOpen(false)}
        onSubmit={handleSaveAssignment}
        setDraft={setAssignmentDraft}
      />

      <ConfirmDeleteModal
        isOpen={Boolean(deleteProfileId)}
        title="Delete callback profile?"
        message="Deleting this callback profile removes it from all new webhook deliveries."
        confirmLabel="Delete profile"
        confirming={deletingProfile}
        onCancel={() => setDeleteProfileId(null)}
        onConfirm={handleDeleteProfile}
      />

      <ConfirmDeleteModal
        isOpen={Boolean(deleteAssignmentId)}
        title="Delete event assignment?"
        message="Deleting this event assignment stops future matching webhook events from being delivered to this profile."
        confirmLabel="Delete assignment"
        confirming={deletingAssignment}
        onCancel={() => setDeleteAssignmentId(null)}
        onConfirm={handleDeleteAssignment}
      />
    </>
  );
}
